import { ringDistance } from '../world/grid.js';
/**
 * Melee combat: a hit per attack interval, for the attacker's damage per
 * second over that interval, less what the target's defence absorbs (ADR
 * 0015). Health, dps and defence are the only stats; a player's blow may
 * land a critical hit, the same chance for everyone (ADR 0049), and nothing
 * dodges. Everything here is the single source of truth for both the server
 * (authority) and the client (previews, never results).
 */
/**
 * Melee reaches the eight neighbouring tiles, diagonals included, so a pack
 * can surround its prey instead of queueing on four sides.
 */
export const MELEE_RANGE = 1;
export const PLAYER_ATTACK_INTERVAL_MS = 1000;
/** How long a corpse stays on screen before it is removed: the death animation. */
export const DEATH_LINGER_MS = 720;
/** A dead player comes back at the zone spawn after this delay. */
export const PLAYER_RESPAWN_MS = 3000;
export function isInMeleeRange(a, b) {
    return ringDistance(a, b) <= MELEE_RANGE;
}
/**
 * What one swing deals: the dps spread over the attack interval, at least a
 * point. At one swing a second it is the dps itself: 12 for a naked level-1
 * warrior, four hits on a 48 hp rat.
 */
export function playerDamage(stats) {
    return Math.max(1, Math.round((stats.dps * PLAYER_ATTACK_INTERVAL_MS) / 1000));
}
/**
 * A player's blow, swing or spell, lands a critical hit one time in ten for
 * half as much again (ADR 0049). It adds 5 % to the average blow, a price
 * paid on purpose for a big number now and then; a creature's blows never
 * crit. These are the bases: affixes raise both (ADR 0057, `combatStatsOf`).
 */
export const CRIT_CHANCE = 0.1;
export const CRIT_MULTIPLIER = 1.5;
/**
 * Roll whether `amount` crits, before the target's defence takes its share,
 * with the attacker's chance and multiplier.
 */
export function rollCrit(amount, rng, crit = {
    critChance: CRIT_CHANCE,
    critMultiplier: CRIT_MULTIPLIER,
}) {
    if (amount <= 0 || rng() >= crit.critChance)
        return { amount, crit: false };
    return { amount: Math.round(amount * crit.critMultiplier), crit: true };
}
/** A spell scales the damage left after the creature's protection. */
export function damageToMob(definition, amount, multiplier = 1) {
    if (amount <= 0)
        return 0;
    return Math.max(1, Math.round(amount - definition.defence * multiplier));
}
/**
 * What a blow of `amount` takes from a creature on average, the critical
 * included: the crit multiplies the blow before the creature's defence takes
 * its share, as `rollCrit` then `damageToMob` do in the room, so against
 * armour it is worth more than its 5 %. For estimates (the pacing audits),
 * never for a real blow.
 */
export function expectedDamageToMob(definition, amount, crit = {
    critChance: CRIT_CHANCE,
    critMultiplier: CRIT_MULTIPLIER,
}) {
    const plain = damageToMob(definition, amount);
    const critical = damageToMob(definition, Math.round(amount * crit.critMultiplier));
    return plain + crit.critChance * (critical - plain);
}
/**
 * Defence against an attacker of `level`: `DEFENCE_PER_LEVEL * level +
 * DEFENCE_BASE` points halve its blows. Diminishing returns, so it never
 * reaches 100 %, and measured against the attacker's level, so armour that
 * turns the meadow's rats harmless still leaves a yeti a threat. The base
 * is large and the slope gentle (ADR 0015, 2026-09-30): 260 points halve a
 * level-20 blow, so the endgame's armour counts, while the marsh still asks
 * for a forged kit.
 */
export const DEFENCE_PER_LEVEL = 9;
export const DEFENCE_BASE = 80;
/** Share of a blow the defence absorbs, in [0, 1). */
export function mitigation(defence, attackerLevel) {
    const armour = Math.max(0, defence);
    const level = Math.max(1, attackerLevel);
    return armour / (armour + DEFENCE_PER_LEVEL * level + DEFENCE_BASE);
}
/**
 * What a mob's blow deals to a target with `defence`. A mob that hits at all
 * still deals a point: armour makes a fight safe, never untouchable.
 */
export function mobDamage(definition, defence = 0) {
    if (definition.damage <= 0)
        return 0;
    const absorbed = mitigation(defence, definition.level ?? 1);
    return Math.max(1, Math.round(definition.damage * (1 - absorbed)));
}
/** Apply `amount` to `hp`; never below zero, and a hit on a corpse does nothing. */
export function applyHit(hp, amount) {
    if (hp <= 0)
        return { amount: 0, hp: 0, killed: false };
    const remaining = Math.max(0, hp - Math.max(0, Math.floor(amount)));
    return { amount: hp - remaining, hp: remaining, killed: remaining === 0 };
}
/**
 * Levels a creature may stand over a player before the gap tells (ADR 0089):
 * bosses and legendary elites stand three over their band, and no fight of a
 * quest chain asks for more (`pnpm audit:quests`).
 */
export const LEVEL_GAP_FREE = 3;
/** Share of a player's damage each level past the free gap takes away. */
export const LEVEL_GAP_DEALT_STEP = 0.1;
/** The least share of their damage a player keeps, however wide the gap. */
export const LEVEL_GAP_DEALT_FLOOR = 0.25;
/** Share a creature's blow gains for each level past the free gap. */
export const LEVEL_GAP_TAKEN_STEP = 0.15;
/**
 * The same steps against a boss (zone or dungeon), twice as steep: a boss
 * closes its region and is met at its level or in a group, never seven
 * levels early (SHE-148: a level-16 Warrior felled the level-23 Frost Giant alone).
 */
export const LEVEL_GAP_BOSS_DEALT_STEP = 0.2;
export const LEVEL_GAP_BOSS_TAKEN_STEP = 0.3;
/** Levels a creature of `mobLevel` stands past the free gap over a player of `playerLevel`. */
export function levelGapExcess(mobLevel, playerLevel) {
    return Math.max(0, Math.floor(mobLevel) - Math.floor(playerLevel) - LEVEL_GAP_FREE);
}
/**
 * What a player's blow, spell or bleed keeps against a creature far over
 * them (ADR 0089). Spells and a bleed that goes through armour let a geared
 * level-11 Warrior fell a level-20 yeti in ten seconds: nothing but the
 * armour curve looked at levels. Never raised the other way. Twice as steep
 * against a boss.
 */
export function levelGapDealt(mobLevel, playerLevel, boss = false) {
    const excess = levelGapExcess(mobLevel, playerLevel);
    const step = boss ? LEVEL_GAP_BOSS_DEALT_STEP : LEVEL_GAP_DEALT_STEP;
    return Math.max(LEVEL_GAP_DEALT_FLOOR, 1 - step * excess);
}
/** What a creature far over a player multiplies its blow by (ADR 0089); twice as steep for a boss. */
export function levelGapTaken(mobLevel, playerLevel, boss = false) {
    const step = boss ? LEVEL_GAP_BOSS_TAKEN_STEP : LEVEL_GAP_TAKEN_STEP;
    return 1 + step * levelGapExcess(mobLevel, playerLevel);
}
//# sourceMappingURL=combat.js.map